// FILE: src/components/MemberProfile.js
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";

export default function MemberProfile() {
  const { user, role, loading } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setProfile(null);
        setPageLoading(false);
        return;
      }

      if (role && role !== "member") {
        setProfile(null);
        setPageLoading(false);
        return;
      }

      setPageLoading(true);
      setMessage("");

      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          setProfile({ id: snap.id, ...snap.data() });
        } else {
          setProfile({
            id: user.uid,
            uid: user.uid,
            email: user.email || "",
            firstName: "",
            lastName: "",
            cellNumber: "",
            homeAddress: "",
            role: "member",
          });
        }
      } catch (err) {
        console.error("Error loading profile:", err);
        setMessage("Could not load profile.");
        setProfile(null);
      } finally {
        setPageLoading(false);
      }
    };

    if (!loading) fetchProfile();
  }, [user, role, loading]);

  const handleChange = (field, value) => {
    setProfile((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user || !profile) return;

    setSaving(true);
    setMessage("");

    const { firstName, lastName, cellNumber, homeAddress } = profile;

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          uid: user.uid,
          email: user.email || "",
          firstName: firstName || "",
          lastName: lastName || "",
          displayName: `${firstName || ""} ${lastName || ""}`.trim(),
          cellNumber: cellNumber || "",
          homeAddress: homeAddress || "",
          role: "member",
        },
        { merge: true }
      );

      const memberRef = doc(db, "members", user.uid);
      await setDoc(
        memberRef,
        {
          uid: user.uid,
          email: user.email || "",
          firstName: firstName || "",
          lastName: lastName || "",
          cellNumber: cellNumber || "",
          homeAddress: homeAddress || "",
        },
        { merge: true }
      );

      setMessage("Profile updated successfully.");
    } catch (err) {
      console.error("Error saving profile:", err);
      setMessage("Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <p>Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <p>You need to be logged in to view your profile.</p>
        <button
          onClick={() => navigate("/login")}
          className="mt-3 px-4 py-2 rounded bg-indigo-600 text-white text-sm"
        >
          Go to Login
        </button>
      </div>
    );
  }

  if (role !== "member") {
    return <Navigate to="/dashboard" replace />;
  }

  if (pageLoading) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <p>Loading profile…</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <p>Profile could not be loaded.</p>
        {message && <p className="mt-2 text-sm text-red-600">{message}</p>}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">My Profile</h2>

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">First Name</label>
          <input
            type="text"
            value={profile.firstName || ""}
            onChange={(e) => handleChange("firstName", e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Last Name</label>
          <input
            type="text"
            value={profile.lastName || ""}
            onChange={(e) => handleChange("lastName", e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            value={profile.email || user.email || ""}
            disabled
            className="w-full border rounded px-3 py-2 text-sm bg-gray-100 cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Cell Number</label>
          <input
            type="tel"
            value={profile.cellNumber || ""}
            onChange={(e) => handleChange("cellNumber", e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Home Address</label>
          <textarea
            rows={3}
            value={profile.homeAddress || ""}
            onChange={(e) => handleChange("homeAddress", e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>

        {/* ✅ POPIA notice */}
        <p className="text-xs text-gray-500 text-center">
          Your personal information is protected and handled in accordance with
          the Protection of Personal Information Act (POPIA). We do not share
          your details with third parties without your consent.
        </p>
      </form>

      {message && <p className="mt-3 text-sm text-gray-700">{message}</p>}
    </div>
  );
}


